/**
 * HomePage — consolidated dashboard that replaces 4 separate dashboard pages
 * (Quick Snapshot, Overview, Executive Dashboard, Command Center) with a single
 * tabbed interface so owners/finance see ONE landing page instead of getting
 * lost across 4 routes.
 *
 * Tabs are lazy-mounted: switching to a tab renders the underlying page
 * component only when first activated, so we don't fetch 4 sets of data on
 * initial paint.
 */
import { useState, lazy, Suspense } from 'react';
import { Activity, Home, Crown, Zap, RefreshCw } from 'lucide-react';
import TodayAttendanceWidget from '../components/TodayAttendanceWidget';

const BusinessHealthPage = lazy(() => import('./BusinessHealthPage'));
const OverviewPage = lazy(() => import('./OverviewPage'));
const ExecutiveDashboard = lazy(() => import('./ExecutiveDashboard'));
const CommandCenterPage = lazy(() => import('./CommandCenterPage'));

const TABS = [
  { id: 'snapshot', label: 'Quick Snapshot', icon: Activity, component: BusinessHealthPage, description: '9 metrik kesihatan kewangan' },
  { id: 'overview', label: 'Overview', icon: Home, component: OverviewPage, description: 'Ringkasan transaksi & trend' },
  { id: 'executive', label: 'Executive Dashboard', icon: Crown, component: ExecutiveDashboard, description: 'KPI tahap tinggi untuk Director' },
  { id: 'command', label: 'Command Center', icon: Zap, component: CommandCenterPage, description: 'Smart alerts, goals & cash forecast' },
];

const FallbackLoader = () => (
  <div data-testid="home-tab-loading" className="flex items-center justify-center py-16">
    <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
    <span className="ml-2 text-sm text-gray-500">Memuat tab…</span>
  </div>
);

const HomePage = () => {
  const [activeTab, setActiveTab] = useState('snapshot');
  // Track mounted tabs so we don't re-mount / refetch every click
  const [mountedTabs, setMountedTabs] = useState({ snapshot: true });

  const switchTab = (id) => {
    setActiveTab(id);
    setMountedTabs((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <div className="space-y-5" data-testid="home-page">
      {/* Today's Attendance — quick snapshot at top */}
      <TodayAttendanceWidget />

      {/* Tabs bar */}
      <div
        data-testid="home-tabs"
        className="flex gap-1 bg-white rounded-xl p-1.5 border border-gray-100 shadow-sm overflow-x-auto"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              data-testid={`home-tab-${t.id}`}
              className={`flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${
                active
                  ? 'bg-red-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Sub-description */}
      <p className="text-xs text-gray-500 px-1">
        {TABS.find((t) => t.id === activeTab)?.description}
      </p>

      {/* Tab bodies — keep mounted for cache, toggle visibility */}
      <Suspense fallback={<FallbackLoader />}>
        {TABS.map((t) => {
          if (!mountedTabs[t.id]) return null;
          const Component = t.component;
          return (
            <div
              key={t.id}
              style={{ display: activeTab === t.id ? 'block' : 'none' }}
              data-testid={`home-tab-panel-${t.id}`}
            >
              <Component />
            </div>
          );
        })}
      </Suspense>
    </div>
  );
};

export default HomePage;
